import { shouldPrefetchAppBootstrap } from "@repo/shared";
import type { ClientAccessSettings } from "@repo/shared/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router";
import { getAppBootstrap } from "@/lib/api/app-bootstrap";
import { useSession } from "@/lib/auth-client";
import { getTenantHostContext } from "@/lib/tenant-host";

const APP_BOOTSTRAP_KEY = ["app-bootstrap"] as const;

export function useAppBootstrap() {
  const { data: session, isPending } = useSession();
  const { pathname } = useLocation();
  const hostContext = useMemo(getTenantHostContext, []);

  return useQuery({
    queryKey: APP_BOOTSTRAP_KEY,
    queryFn: getAppBootstrap,
    // QL-628 — a `null` cached from a signed-out cold load (see getAppBootstrap)
    // must never be served as fresh to a later signed-in render: it is not a
    // query error, so a normal render-time guard can't tell it apart from "not
    // signed in" otherwise. Force an immediate refetch in that case; a real
    // bootstrap keeps the usual 5-minute staleTime.
    staleTime: (query) => (query.state.data === null ? 0 : 5 * 60 * 1000),
    // QL-628 — on a cold load of a protected route the bootstrap is requested in
    // parallel with get-session instead of after it (one round trip less to the
    // first render). A 401 resolves to null (see getAppBootstrap). Once the
    // session is known the rule is unchanged: fetch iff signed in.
    enabled:
      !!session ||
      shouldPrefetchAppBootstrap({
        sessionPending: isPending,
        hostKind: hostContext.kind,
        pathname,
      }),
  });
}

/**
 * QL-628 — on sign-out (an SPA navigation, so the QueryClient survives) mark the
 * bootstrap as signed-out (`null`) instead of removing it: the null-staleTime rule
 * above forces a refetch on the next signed-in render, and AppShell keeps its
 * spinner while that refetch runs, without a spurious fetch during sign-out.
 */
export function useMarkAppBootstrapSignedOut() {
  const qc = useQueryClient();
  return useCallback(() => qc.setQueryData(APP_BOOTSTRAP_KEY, null), [qc]);
}

const CLIENT_ACCESS_BOTH_OFF: ClientAccessSettings = {
  privateEnabled: false,
  teamAccessEnabled: false,
};

/** QL-380 — the tenant's client access settings; both off until the bootstrap has loaded. */
export function useClientAccessSettings(): ClientAccessSettings {
  return useAppBootstrap().data?.clientAccess ?? CLIENT_ACCESS_BOTH_OFF;
}
